import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Typography, message } from 'antd';
import { useSearchParams } from 'react-router-dom';
import cccApi, { RetencaoMarcacao } from '../api/cccApi';
import RetencaoWorkspaceTables from '../components/RetencaoWorkspaceTables';
import WonderTradeLoader from '@crm/components/Shared/WonderTradeLoader';
import { normalizeWorkspaceSubstage } from '../config/retencaoWorkspace';
import { getWorkspaceSubstage } from '../utils/retencaoMarcacaoDisplay';
import '../styles/recuperacao.css';

const { Text } = Typography;

/** Marcações em Reposição (pool/workspace) — used for deep links from dashboard/calendar. */
const RetencaoMarcacaoBoard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<RetencaoMarcacao[]>([]);
  const [retrocederTargetId, setRetrocederTargetId] = useState<string | null>(null);
  const [retrocedendo, setRetrocedendo] = useState(false);
  const [workspaceFocus, setWorkspaceFocus] = useState<{
    marcacaoId: string;
    substage: string;
    infoTab?: 'historico';
  } | null>(null);
  const urlFocusHandledRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cccApi.getRetencaoMarcacoes();
      const list = Array.isArray(res.marcacoes) ? res.marcacoes : [];
      setItems(list);
    } catch {
      message.error('Não foi possível carregar marcações de Reposição.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const mine = useMemo(() => items.filter((m) => m.is_mine), [items]);

  useEffect(() => {
    const marcacaoId = searchParams.get('marcacao')?.trim();
    if (!marcacaoId) {
      urlFocusHandledRef.current = null;
      return;
    }
    if (loading) return;

    const applyFocus = (m: RetencaoMarcacao) => {
      const tab = searchParams.get('tab')?.trim();
      setWorkspaceFocus({
        marcacaoId,
        substage: normalizeWorkspaceSubstage(getWorkspaceSubstage(m)),
        ...(tab === 'historico' ? { infoTab: 'historico' as const } : {}),
      });
    };

    const m = mine.find((x) => (x.marcacao_id || String(x.id)) === marcacaoId);
    if (m) {
      urlFocusHandledRef.current = marcacaoId;
      applyFocus(m);
      return;
    }

    if (urlFocusHandledRef.current === marcacaoId) return;
    urlFocusHandledRef.current = marcacaoId;

    let cancelled = false;
    (async () => {
      try {
        const res = await cccApi.getRetencaoMarcacao(marcacaoId);
        if (cancelled || !res.success || !res.marcacao?.is_mine) return;
        await load();
        if (cancelled) return;
        applyFocus(res.marcacao);
      } catch {
        /* list may still load on next refresh */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, mine, loading, load]);

  const handleSubstageChange = async (marcacaoId: string, substage: string): Promise<boolean> => {
    try {
      const res = await cccApi.updateRetencaoSubstage(marcacaoId, substage);
      if (!res.success) {
        message.error(res.message || 'Erro ao atualizar estado');
        return false;
      }
      message.success('Estado atualizado');
      await load();
      return true;
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao atualizar estado';
      message.error(msg);
      return false;
    }
  };

  const handleAddNote = async (marcacaoId: string, text: string) => {
    try {
      const res = await cccApi.addRetencaoNote(marcacaoId, text);
      if (res.success) {
        message.success('Nota adicionada');
        await load();
      }
    } catch {
      message.error('Não foi possível adicionar a nota');
    }
  };

  const confirmRetroceder = async () => {
    if (!retrocederTargetId) return;
    setRetrocedendo(true);
    try {
      await cccApi.releaseRetencaoMarcacao(retrocederTargetId);
      message.success('Marcação devolvida ao pool');
      setRetrocederTargetId(null);
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Não foi possível retroceder a marcação';
      message.error(msg);
    } finally {
      setRetrocedendo(false);
    }
  };

  if (loading && mine.length === 0) {
    return (
      <div className="rec-desk-empty">
        <WonderTradeLoader message="A carregar Reposição…" />
      </div>
    );
  }

  return (
    <div className="rec-page">
      <div className="rec-desk">
        <div className="rec-desk-body">
          {mine.length === 0 ? (
            <div className="rec-desk-empty">
              <p className="rec-desk-empty-title">Área de trabalho vazia</p>
              <Text type="secondary">
                Esta marcação não está na sua área de trabalho ou ainda não foi atribuída.
              </Text>
            </div>
          ) : (
            <RetencaoWorkspaceTables
              items={mine}
              onSubstageChange={handleSubstageChange}
              onRetroceder={setRetrocederTargetId}
              onAddNote={handleAddNote}
              onMarcacaoRefresh={load}
              workspaceFocus={workspaceFocus}
              onWorkspaceFocusDone={() => setWorkspaceFocus(null)}
            />
          )}
        </div>
      </div>

      {retrocederTargetId && (
        <div className="rec-modal-backdrop" role="presentation">
          <div className="rec-modal" role="dialog" aria-modal="true" aria-labelledby="retencao-retroceder-title">
            <h3 id="retencao-retroceder-title">Retroceder marcação?</h3>
            <p>A marcação volta ao pool e deixa de estar na sua área de trabalho.</p>
            <div className="rec-modal-actions">
              <button
                type="button"
                className="rec-modal-btn rec-modal-btn--ghost"
                disabled={retrocedendo}
                onClick={() => setRetrocederTargetId(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="rec-modal-btn rec-modal-btn--danger"
                disabled={retrocedendo}
                onClick={() => void confirmRetroceder()}
              >
                {retrocedendo ? 'A retroceder…' : 'Retroceder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetencaoMarcacaoBoard;
